import { SearchInput } from "../../common/ClearControls";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import axios from "axios";
import { FiCamera, FiCheck, FiEye, FiPlus, FiSearch, FiTrash2, FiUpload, FiX } from "react-icons/fi";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import styles from "./patientDetails.module.scss";

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(value);

export function NonDentallyPatientInformation({ initialPatient = {}, disabled = false }) {
  const [details, setDetails] = useState(() => ({
    name: [initialPatient.title, initialPatient.first_name, initialPatient.last_name].filter(Boolean).join(" "),
    mobile: initialPatient.mobile_phone || "",
    email: initialPatient.email_address || "",
    address: [initialPatient.address_line_1, initialPatient.address_line_2, initialPatient.town, initialPatient.postcode].filter(Boolean).join(", "),
  }));
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState("");
  const [screenshotError, setScreenshotError] = useState("");
  const screenshotInput = useRef(null);

  useEffect(() => {
    if (!screenshot) {
      setPreview("");
      return undefined;
    }
    const url = URL.createObjectURL(screenshot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  const selectScreenshot = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      setScreenshotError("Choose a PNG or JPG image smaller than 5 MB.");
      return;
    }
    setScreenshotError("");
    setScreenshot(file);
  };

  return (
    <section className={styles.manualPatientCard} aria-labelledby="manual-patient-heading">
      <div className={styles.manualSectionHeading}>
        <h3 id="manual-patient-heading">Patient information</h3>
        <button type="button" className={styles.manualOutlineButton} disabled={disabled} onClick={() => setShowScreenshot(true)}>
          <FiCamera aria-hidden="true" /> Autofill from screenshot
        </button>
      </div>
      <div className={styles.manualAiNotice}>
        <AutoAwesomeOutlinedIcon fontSize="small" aria-hidden="true" />
        <p><strong>AI disclaimer:</strong> upload a screenshot of the patient&apos;s record and AI will fill in the fields below. AI can make mistakes — always check the details are correct before sending.</p>
      </div>
      <div className={styles.manualPatientGrid}>
        {[
          ["name", "Name", "text", "Patient full name", "name"],
          ["mobile", "Mobile", "tel", "07...", "tel"],
          ["email", "Email", "email", "patient@example.com", "email"],
          ["address", "Address", "text", "Street, town, postcode", "street-address"],
        ].map(([key, label, type, placeholder, autoComplete]) => (
          <label key={key} className={`${styles.manualField} ${key === "email" || key === "address" ? styles.manualFullWidth : ""}`}>
            <span>{label}</span>
            <input type={type} autoComplete={autoComplete} placeholder={placeholder} value={details[key]} disabled={disabled} onChange={(event) => setDetails((current) => ({ ...current, [key]: event.target.value }))} />
          </label>
        ))}
      </div>
      <Dialog open={showScreenshot} onClose={() => setShowScreenshot(false)} maxWidth="sm" fullWidth aria-labelledby="patient-screenshot-title" aria-describedby="patient-screenshot-description" PaperProps={{ className: styles.patientScreenshotDialog }}>
        <div className={styles.manualSectionHeading}>
          <h3 id="patient-screenshot-title">Autofill from screenshot</h3>
          <button type="button" className={styles.manualIconButton} onClick={() => setShowScreenshot(false)} aria-label="Close screenshot preview"><FiX /></button>
        </div>
        <p id="patient-screenshot-description" className={styles.manualHint}>Automatic autofill is coming soon. For now, preview your screenshot and enter the patient details manually.</p>
        <input ref={screenshotInput} hidden type="file" accept="image/png,image/jpeg" aria-label="Patient screenshot" onChange={selectScreenshot} />
        <button type="button" className={styles.screenshotPicker} onClick={() => screenshotInput.current?.click()}>
          <FiUpload aria-hidden="true" /><strong>{screenshot ? "Choose another screenshot" : "Choose a screenshot"}</strong><span>PNG or JPG, up to 5 MB</span>
        </button>
        {screenshotError && <p className={styles.manualError} role="alert">{screenshotError}</p>}
        {preview && screenshot && <figure className={styles.patientScreenshotPreview}><img src={preview} alt="Selected patient record screenshot" /><figcaption>{screenshot.name}</figcaption></figure>}
        <div className={styles.manualFormActions}>
          {screenshot && <button type="button" className={styles.manualTextButton} onClick={() => { setScreenshot(null); setScreenshotError(""); }}>Remove screenshot</button>}
          <button type="button" className={styles.manualPrimaryButton} onClick={() => setShowScreenshot(false)}>Done</button>
        </div>
      </Dialog>
    </section>
  );
}

export function NonDentallyTreatmentPlans({
  consentBundleId,
  consentForm,
  onTemplatesSaved,
  onSavingChange,
  disabled = false,
}) {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(Boolean(consentBundleId));
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [retry, setRetry] = useState(0);
  const [savingId, setSavingId] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [prices, setPrices] = useState({});
  const savingRef = useRef(false);

  useEffect(() => {
    if (!consentBundleId) {
      setLoading(false);
      setLoadError("Select a consent bundle to load templates.");
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setLoadError("");
    setTemplates([]);

    axios.get(
      `/consent_bundle/${consentBundleId}/get_available_consent_clauses`,
      { signal: controller.signal }
    ).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(data)) throw new Error("Invalid template response");
      setTemplates(data);
    }).catch(() => {
      if (!controller.signal.aborted) {
        setLoadError("Unable to load templates. Please try again.");
      }
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });

    return () => controller.abort();
  }, [consentBundleId, retry]);

  const selectedTemplates = useMemo(() => {
    const seen = new Set();
    return (consentForm?.clause_blocks || [])
      .filter((block) => {
        if (["intro", "outro", "general"].includes(block.type) || block.template_id == null) return false;
        const id = String(block.template_id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((block) => {
        const template = templates.find((item) => String(item.id) === String(block.template_id));
        return {
          ...template,
          id: block.template_id,
          name: block.name || template?.name || "Untitled template",
          body: block.content ?? template?.body ?? "",
        };
      });
  }, [consentForm, templates]);

  const selectedIds = new Set(selectedTemplates.map((template) => String(template.id)));
  const query = search.trim().toLowerCase();
  const groupedTemplates = templates
    .filter((template) => (template.name || "").toLowerCase().includes(query))
    .reduce((groups, template) => {
      const type = template.type || "Main";
      if (!groups[type]) groups[type] = [];
      groups[type].push(template);
      return groups;
    }, {});

  const getPrice = (template) => {
    if (prices[String(template.id)] !== undefined) return prices[String(template.id)];
    const price = Number(template.price ?? 0);
    return (Number.isFinite(price) && price >= 0 ? price : 0).toFixed(2);
  };
  const total = selectedTemplates.reduce((sum, template) => sum + Number(getPrice(template) || 0), 0);
  const busy = disabled || savingId !== null;

  const saveSelection = async (template, remove = false) => {
    const id = String(template.id);
    if (busy || savingRef.current || !consentBundleId || (!remove && selectedIds.has(id))) return;

    const ids = remove
      ? Array.from(selectedIds).filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];

    savingRef.current = true;
    setSavingId(id);
    setSaveError("");
    onSavingChange?.(true);

    try {
      const { data } = await axios.put(
        `/consent_bundle/${consentBundleId}/update_consent_form_with_selected_templates`,
        { block_ids: ids },
        { headers: { "Content-Type": "application/json" } }
      );
      // Some API versions return an acknowledgement instead of the updated bundle.
      const bundle = data?.consent_form
        ? data
        : (await axios.get(`/consent_bundle/${consentBundleId}`)).data;
      if (!bundle?.consent_form) throw new Error("Missing updated consent form");
      onTemplatesSaved(bundle);
    } catch {
      setSaveError(`Unable to ${remove ? "remove" : "add"} this template. Please try again.`);
    } finally {
      savingRef.current = false;
      setSavingId(null);
      onSavingChange?.(false);
    }
  };

  return (
    <section className={styles.manualTreatmentSection} aria-labelledby="manual-treatments-heading">
      <h3 id="manual-treatments-heading">Treatment Plans</h3>
      <p className={styles.manualHint}>Search templates by name and add them to your treatment plan. Prices can be changed.</p>
      <div className={styles.manualTemplateCard}>
        <h4 className={styles.manualAddTreatmentTitle}>Add treatments</h4>
        <label className={styles.manualTemplateSearch}>
          <FiSearch aria-hidden="true" />
          <SearchInput onClear={() => { setSearch(""); }}
            type="search"
            aria-label="Search templates by name"
            placeholder="Search templates..."
            value={search}
            disabled={disabled}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        {query && (
        <div className={styles.manualTemplateResults} aria-busy={loading}>
          {loading ? (
            <p className={styles.manualEmptyText} role="status">Loading templates...</p>
          ) : loadError ? (
            <div>
              <p className={styles.manualError} role="alert">{loadError}</p>
              {consentBundleId && <button type="button" className={styles.manualTextButton} disabled={busy} onClick={() => setRetry((value) => value + 1)}>Retry loading templates</button>}
            </div>
          ) : Object.keys(groupedTemplates).length === 0 ? (
            <p className={styles.manualEmptyText} role="status">{query ? "No matching templates found." : "No templates are available for this consent bundle."}</p>
          ) : (
            Object.entries(groupedTemplates).map(([type, items]) => (
              <div key={type} className={styles.manualTemplateGroup}>
                <div className={styles.manualTemplateGroupHeading}><h5>{type} templates</h5><span>{items.length}</span></div>
                <ul className={styles.manualTemplateList} aria-label={`${type} templates`}>
                  {items.map((template) => {
                    const added = selectedIds.has(String(template.id));
                    return (
                      <li key={template.id}>
                        <div><strong>{template.name || "Untitled template"}</strong></div>
                        <button type="button" className={styles.manualIconButton} onClick={() => setPreviewTemplate(template)} aria-label={`Preview ${template.name}`} title="Preview template"><FiEye aria-hidden="true" /></button>
                        <button type="button" className={styles.manualPrimaryButton} disabled={busy || added} onClick={() => saveSelection(template)} aria-label={`Add ${template.name} template`}>
                          {added ? <FiCheck aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
                          {added ? "Added" : savingId === String(template.id) ? "Adding..." : "Add Template"}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
        )}
        {saveError && <p className={styles.manualError} role="alert">{saveError}</p>}
      </div>
      {selectedTemplates.length > 0 && (
        <div className={styles.manualPlanCard} aria-busy={savingId !== null}>
          <h4>Added treatments</h4>
          <ul className={styles.manualTemplateBubbles} aria-label="Added treatments">
            {selectedTemplates.map((template) => (
              <li key={template.id} className={styles.manualTemplateBubble}>
                <div className={styles.manualTemplateBubbleHeading}>
                  <strong>{template.name}</strong>
                  <button type="button" className={styles.manualIconButton} onClick={() => setPreviewTemplate(template)} aria-label={`View ${template.name} template`} title="View template"><FiEye aria-hidden="true" /></button>
                  <button type="button" className={styles.manualIconButton} disabled={busy} onClick={() => saveSelection(template, true)} aria-label={`Remove ${template.name}`} title="Remove template"><FiTrash2 aria-hidden="true" /></button>
                </div>
                <label className={styles.manualPriceField}>
                  <span>Price (£)</span>
                  <input type="number" min="0" step="0.01" inputMode="decimal" value={getPrice(template)} disabled={busy} aria-label={`Price for ${template.name}`} onChange={(event) => {
                    const value = event.target.value;
                    if (value !== "" && (!Number.isFinite(Number(value)) || Number(value) < 0)) return;
                    setPrices((current) => ({ ...current, [String(template.id)]: value }));
                  }} onBlur={() => setPrices((current) => ({ ...current, [String(template.id)]: Number(getPrice(template) || 0).toFixed(2) }))} />
                </label>
              </li>
            ))}
          </ul>
          <div className={styles.manualPlanTotal}><span>Total</span><strong>{money(total)}</strong></div>
        </div>
      )}
      <Dialog open={Boolean(previewTemplate)} onClose={() => setPreviewTemplate(null)} maxWidth="md" fullWidth aria-labelledby="treatment-template-preview-title" PaperProps={{ className: styles.manualTemplatePreviewDialog }}>
        <div className={styles.manualSectionHeading}>
          <h3 id="treatment-template-preview-title">{previewTemplate?.name || "Template preview"}</h3>
          <button type="button" className={styles.manualIconButton} onClick={() => setPreviewTemplate(null)} aria-label="Close template preview"><FiX aria-hidden="true" /></button>
        </div>
        {previewTemplate && (
          previewTemplate.body ? (
            <iframe
              className={styles.manualTemplatePreviewContent}
              title={`Content of ${previewTemplate.name}`}
              sandbox=""
              srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{font:14px/1.6 Arial,sans-serif;color:#2c2c2c;overflow-wrap:anywhere;margin:16px}img{max-width:100%;height:auto}table{max-width:100%;border-collapse:collapse}td,th{border:1px solid #dfe3ea;padding:8px}</style></head><body>${previewTemplate.body}</body></html>`}
            />
          ) : <p className={styles.manualHint}>No content is available for this template.</p>
        )}
        <div className={styles.manualFormActions}><button type="button" className={styles.manualPrimaryButton} onClick={() => setPreviewTemplate(null)}>Close</button></div>
      </Dialog>
    </section>
  );
}
